import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import RepairTabs, { type RepairTab } from '../components/RepairTabs';
import { fetchPageContent, type PageSection } from '../../../utils/pageContentStorage';

const TABS: RepairTab[] = [
  {
    key: 'screen',
    label: '螢幕更換',
    image: 'https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/41095147-dc2e-4190-9248-4f9c0fcf3f81_SnapInsta.to_652042133_18159042856428190_3299029170784109175_n.jpg?v=c2ce3c0a3e3e77978ad0634d2776e97c',
    objectPosition: 'center',
    priceFrom: 'NT$3,200 起',
    time: '2–4 小時',
    warranty: '3 個月',
    features: [
      { point: 'iPad 全系列螢幕更換', detail: 'iPad Pro、Air、Mini、標準版均可處理，備有主流機型現貨。' },
      { point: '更換後觸控全面積測試', detail: '確認觸控回應均勻，無死角、邊緣失靈問題。' },
      { point: '貼合工藝精準', detail: '螢幕邊框貼合嚴密，防止灰塵進入與邊緣翹起問題。' },
    ],
  },
  {
    key: 'battery',
    label: '電池更換',
    image: 'https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/9697daf2-0ee7-4efb-b81f-76231f37edb0_IMG_8511.jpeg?v=91bf96d394311b39a88f8412ce6fdfb3',
    priceFrom: 'NT$1,200 起',
    time: '1–2 小時',
    warranty: '3 個月',
    features: [
      { point: '高容量電芯，符合原廠規格', detail: 'iPad 電池容量大，選用高品質電芯確保續航正常。' },
      { point: '更換後電量校正', detail: '解決電量顯示不準、電量跳動等老化問題。' },
      { point: '充電測試完成後交件', detail: '確認充電正常、無異常發熱後才交回給您。' },
    ],
  },
  {
    key: 'charge',
    label: '充電口 / 按鍵',
    image: 'https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/46360a70-5c6e-4b4e-bb46-47531d92dd30_iPad.png?v=8a3b9757d19bbc85073cfa5e3f9eeadc',
    priceFrom: 'NT$800 起',
    time: '1–2 小時',
    warranty: '3 個月',
    features: [
      { point: 'Lightning / USB-C 充電口維修', detail: '接觸不良、充電時斷時續、無法充電均可修復。' },
      { point: 'Home 鍵 / Touch ID 維修', detail: '按鍵失靈或 Touch ID 無反應，可評估更換。' },
      { point: '維修前免費診斷確認', detail: '確定問題來源後才報價，不亂拆、不亂換零件。' },
    ],
  },
  {
    key: 'storage',
    label: '容量升級',
    image: 'https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/ipadstorage.jpg',
    objectPosition: 'center',
    priceFrom: '依機型報價',
    time: '1–3 工作天',
    warranty: '3 個月',
    features: [
      { point: 'iPad 儲存容量升級', detail: '部分 iPad 機型可評估儲存晶片升級，解決空間不足問題。' },
      { point: '升級前資料備份協助', detail: '升級前協助完整備份，確保照片、App 資料不遺失。' },
      { point: '升級後系統功能驗證', detail: '確認 iPadOS 正常、App 資料完整後才交件。' },
    ],
  },
  {
    key: 'board',
    label: '主板 / 進水',
    image: 'https://storage.readdy-site.link/project_files/a6fca87a-8436-4bd7-9e14-084f599afe41/b6d9a4d1-4191-46e8-a399-15126cfe5646_SnapInsta.to_653365630_18114479359635411_727947456638233995_n.jpg?v=85eedf2313ac301e61cea7364cb86516',
    objectPosition: 'center',
    priceFrom: 'NT$2,000 起',
    time: '1–3 工作天',
    warranty: '3 個月',
    features: [
      { point: '進水腐蝕清洗處理', detail: '超音波清洗去除腐蝕物，提高救回成功率。' },
      { point: '主板短路與 IC 修復', detail: '具備顯微焊接能力，可處理一般維修店無法修復的案件。' },
      { point: '修不好不收費', detail: '無法修復的主板問題，免收維修費，只收診斷成本費。' },
    ],
  },
];

export default function IpadServicePage() {
  const navigate = useNavigate();
  const [bannerData, setBannerData] = useState<PageSection | null>(null);

  useEffect(() => {
    fetchPageContent().then(content => {
      setBannerData(content.find(s => s.id === 'ipad-banner') ?? null);
    });
  }, []);

  const bannerTitle = bannerData?.title ?? 'iPad 維修';
  const bannerSubtitle = bannerData?.subtitle ?? 'iPad Pro / Air / Mini 全系列服務';
  const bannerImage = bannerData?.image ?? 'https://ollumiosiejdxqaygglt.supabase.co/storage/v1/object/public/news-images/site-images/ipadhero.jpg';

  const models = ['iPad Pro 12.9" (全系列)', 'iPad Pro 11" (全系列)', 'iPad Air (全系列)', 'iPad Mini (全系列)', 'iPad (第5代至今)'];

  const faqs = [
    { q: 'iPad 螢幕更換後，觸控筆還能用嗎？', a: 'iPad Pro 使用 Apple Pencil，更換符合規格的螢幕後觸控筆功能通常可維持。建議換後現場測試確認。' },
    { q: '進水的 iPad 還有機會救回嗎？', a: '進水越早送修，成功率越高。建議立即關機，不要嘗試充電，盡快帶至本店處理。' },
    { q: 'iPad 螢幕很大，換起來比 iPhone 貴很多？', a: 'iPad 螢幕尺寸較大、技術難度較高，報價會比 iPhone 高。確切費用帶機來店診斷後告知。' },
  ];

  return (
    <>
      <Navbar />
      <main>
        <section className="pt-32 pb-16 bg-gray-950 relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img src={bannerImage} alt="" className="w-full h-full object-cover object-top" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-6">
              <button onClick={() => navigate('/services')} className="text-gray-500 hover:text-orange-400 text-sm cursor-pointer">維修項目</button>
              <i className="ri-arrow-right-s-line text-gray-600" />
              <span className="text-orange-400 text-sm">iPad 維修</span>
            </div>
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 flex items-center justify-center bg-orange-500/20 border border-orange-500/30 rounded-2xl">
                <i className="ri-tablet-line text-orange-400 text-3xl" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white">{bannerTitle}</h1>
                <p className="text-gray-400 mt-1">{bannerSubtitle}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-3 gap-12">
              <div className="lg:col-span-2">
                <h2 className="text-xl font-bold text-gray-900 mb-6">選擇維修項目</h2>
                <RepairTabs
                  tabs={TABS}
                  note="iPad 螢幕尺寸與機型眾多，實際報價依機型確認後提供，歡迎帶機到店免費診斷。"
                />
              </div>
              <div className="space-y-5">
                <div className="bg-gray-950 rounded-2xl p-6 text-white">
                  <h3 className="font-bold mb-1 text-sm text-orange-400 uppercase tracking-wide">支援機型</h3>
                  <p className="text-gray-400 text-xs mb-4">以下所有系列均支援</p>
                  <ul className="space-y-2">
                    {models.map(m => (
                      <li key={m} className="flex items-center gap-2 text-sm text-gray-300">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-orange-400 text-xs" />
                        </div>
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-orange-50 border border-orange-100 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-xl">
                      <i className="ri-shield-check-line text-white text-sm" />
                    </div>
                    <span className="font-bold text-gray-900 text-sm">3 個月保固</span>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed">所有維修項目提供 3 個月保固，因維修部件本身問題免費重修。</p>
                </div>
                <div className="bg-gray-950 rounded-2xl p-5 text-center">
                  <div className="text-white text-sm font-semibold mb-1">免費到店診斷</div>
                  <div className="text-gray-400 text-xs mb-4">診斷清楚再報價，確認才施工</div>
                  <button onClick={() => navigate('/contact')} className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-xl cursor-pointer whitespace-nowrap transition-colors text-sm">立即預約</button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">常見問題</h2>
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-orange-100 rounded-lg flex-shrink-0 mt-0.5">
                      <span className="text-orange-500 text-xs font-bold">Q</span>
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 text-sm mb-2">{f.q}</div>
                      <div className="text-gray-500 text-sm leading-relaxed">{f.a}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-gray-950">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">iPad 出問題了？立刻處理</h2>
            <p className="text-gray-400 text-sm mb-7">免費診斷・透明報價・3 個月保固</p>
            <div className="flex flex-wrap justify-center gap-4">
              <button onClick={() => navigate('/pricing')} className="px-8 py-3.5 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full cursor-pointer whitespace-nowrap transition-colors">查詢維修報價</button>
              <button onClick={() => navigate('/contact')} className="px-8 py-3.5 border border-white/30 text-white hover:bg-white/10 font-semibold rounded-full cursor-pointer whitespace-nowrap transition-colors">立即聯絡</button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
